import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {ImplementedAntibodySchemaInterfacesSummaryQuery} from 'antibodies/hooks/__generated/ImplementedAntibodySchemaInterfacesSummaryQuery';
import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

export type ImplementedAntibodySchemaInterfaceSummary =
  ImplementedAntibodySchemaInterfacesSummaryQuery['allImplementedAntibodySchemaInterfaces'][number];

export const IMPLEMENTED_ANTIBODY_SCHEMA_INTERFACES_SUMMARY_QUERY = gql`
  query ImplementedAntibodySchemaInterfacesSummaryQuery @skipAudit @engComponent(name: ANTIBODIES) {
    allImplementedAntibodySchemaInterfaces {
      id
      format {
        id
      }
      schema {
        id
        name
        archived
      }
    }
  }
`;

/**
 * Lightweight version of useImplementedAntibodySchemaInterfacesQuery that only fetches the schema-to-format
 * mapping (format ID, schema ID/name/archived). Avoids transferring the full format object over the wire.
 */
export function useImplementedAntibodySchemaInterfacesSummaryQuery(): {
  status: QueryResultType;
  implementedAntibodySchemaInterfaces: Array<ImplementedAntibodySchemaInterfaceSummary>;
  refetch: () => Promise<Array<ImplementedAntibodySchemaInterfaceSummary>>;
} {
  const result = useSkippableBenchlingQuery<ImplementedAntibodySchemaInterfacesSummaryQuery>(
    IMPLEMENTED_ANTIBODY_SCHEMA_INTERFACES_SUMMARY_QUERY,
    {
      skip: !BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES,
    }
  );

  const queryRefetch = result.refetch;

  const refetch = useCallback(async (): Promise<Array<ImplementedAntibodySchemaInterfaceSummary>> => {
    const refetchResult = await queryRefetch();
    return refetchResult.data?.allImplementedAntibodySchemaInterfaces ?? [];
  }, [queryRefetch]);

  if (result.type !== QueryResultType.SUCCESS) {
    return {status: result.type, implementedAntibodySchemaInterfaces: [], refetch};
  }

  return {
    status: QueryResultType.SUCCESS,
    implementedAntibodySchemaInterfaces: result.data.allImplementedAntibodySchemaInterfaces,
    refetch,
  };
}
